/**
 * Roguelite Card Draw — card option types, draw logic, and card application.
 */

import type { GameState } from '../types';
import type { WeaponDef } from '../weapons/types';
import { lookupWeapon, getWeaponsByRarity } from '../weapons/registry';
import { ROGUELITE } from '../constants';
import { lookupModifier, getEligibleModifiers } from './modifier-registry';
import { recompileWeapons } from './weapon-compile';
import { SeededRNG } from '../terrain/rng';

export interface CardOption {
  type: 'weapon' | 'modifier';
  weaponId?: string;
  modifierId?: string;
  rarity: string;
  name: string;
  description: string;
}

/** Draw 3 weapon cards from the pool, rarity-weighted, no duplicates. */
export function drawWeaponCards(state: GameState, zone: number): CardOption[] {
  const seed = (state.terrainSeed ^ (zone * 7919)) >>> 0;
  const rng = new SeededRNG(seed);
  const cards: CardOption[] = [];
  const usedIds = new Set<string>();

  // Build weighted pool
  const pool: WeaponDef[] = [];
  const addByRarity = (rarity: 'common' | 'uncommon' | 'rare' | 'epic', weight: number) => {
    const weapons = getWeaponsByRarity(rarity);
    for (let i = 0; i < weight; i++) pool.push(...weapons);
  };
  addByRarity('common', ROGUELITE.DRAW_WEIGHT_COMMON);
  addByRarity('uncommon', ROGUELITE.DRAW_WEIGHT_UNCOMMON);
  addByRarity('rare', ROGUELITE.DRAW_WEIGHT_RARE);
  addByRarity('epic', ROGUELITE.DRAW_WEIGHT_EPIC);

  if (pool.length === 0) return cards;

  // Pity: if counter >= threshold, force 1 rare+ into draw
  const ms = state.modeState;
  const needsPity = ms.roguelitePityCounter >= ROGUELITE.PITY_THRESHOLD;

  for (let i = 0; i < ROGUELITE.CARD_COUNT; i++) {
    let weapon: WeaponDef | null = null;

    if (i === 0 && needsPity) {
      // Force rare+ for pity
      const rarePool = [...getWeaponsByRarity('rare'), ...getWeaponsByRarity('epic')];
      if (rarePool.length > 0) {
        weapon = rarePool[Math.floor(rng.next() * rarePool.length)];
      }
    }

    // Normal draw
    let attempts = 0;
    while (!weapon || usedIds.has(weapon.id)) {
      weapon = pool[Math.floor(rng.next() * pool.length)];
      attempts++;
      if (attempts > 50) break;
    }

    if (weapon && !usedIds.has(weapon.id)) {
      usedIds.add(weapon.id);
      cards.push({
        type: 'weapon',
        weaponId: weapon.id,
        rarity: weapon.rarity,
        name: weapon.name,
        description: weapon.description,
      });
    }
  }

  return cards;
}

/** Draw 3 modifier cards from the pool, filtered by requirements. */
export function drawModifierCards(state: GameState, zone: number): CardOption[] {
  const seed = (state.terrainSeed ^ (zone * 13331)) >>> 0;
  const rng = new SeededRNG(seed);
  const cards: CardOption[] = [];
  const usedIds = new Set<string>();

  const ms = state.modeState;
  const eligible = getEligibleModifiers(ms, zone);

  if (eligible.length === 0) return cards;

  // Build weighted pool
  const pool: typeof eligible = [];
  const weightMap: Record<string, number> = {
    common: ROGUELITE.DRAW_WEIGHT_COMMON,
    uncommon: ROGUELITE.DRAW_WEIGHT_UNCOMMON,
    rare: ROGUELITE.DRAW_WEIGHT_RARE,
    epic: ROGUELITE.DRAW_WEIGHT_EPIC,
  };
  for (const mod of eligible) {
    const w = weightMap[mod.rarity] ?? ROGUELITE.DRAW_WEIGHT_COMMON;
    for (let i = 0; i < w; i++) pool.push(mod);
  }

  for (let i = 0; i < ROGUELITE.CARD_COUNT; i++) {
    let mod = pool[Math.floor(rng.next() * pool.length)];
    let attempts = 0;
    while (usedIds.has(mod.id)) {
      mod = pool[Math.floor(rng.next() * pool.length)];
      attempts++;
      if (attempts > 50) break;
    }
    if (!usedIds.has(mod.id)) {
      usedIds.add(mod.id);
      cards.push({
        type: 'modifier',
        modifierId: mod.id,
        rarity: mod.rarity,
        name: mod.name,
        description: mod.description,
      });
    }
  }

  return cards;
}

/** Apply the player's card choice. */
export function applyCardChoice(state: GameState, index: number): void {
  const ms = state.modeState;
  const options = ms.rogueliteCardDrawOptions;
  if (!options || index < 0 || index >= options.length) return;

  const card = options[index];

  if (card.type === 'weapon' && card.weaponId) {
    if (ms.rogueliteCardDrawType === 'weapon1') {
      ms.rogueliteShot1Base = card.weaponId;
    } else if (ms.rogueliteCardDrawType === 'weapon2') {
      ms.rogueliteShot2Base = card.weaponId;
    }
    // Track pity
    const isRarePlus = card.rarity === 'rare' || card.rarity === 'epic' || card.rarity === 'legendary';
    ms.roguelitePityCounter = isRarePlus ? 0 : ms.roguelitePityCounter + 1;
    recompileWeapons(ms);
  } else if (card.type === 'modifier' && card.modifierId) {
    const mod = lookupModifier(card.modifierId);
    if (!mod) return;

    if (mod.isUtility && mod.immediateEffect) {
      // Utility mods apply immediately
      mod.immediateEffect(state);
    } else {
      // Assign to shot with fewer mods (or shot 1 if tied)
      const s1Count = ms.rogueliteShot1Mods.length;
      const s2Count = ms.rogueliteShot2Mods.length;
      if (ms.rogueliteShot2Base && s2Count < s1Count && s2Count < 2) {
        ms.rogueliteShot2Mods.push(card.modifierId);
      } else if (s1Count < 2) {
        ms.rogueliteShot1Mods.push(card.modifierId);
      } else if (s2Count < 2 && ms.rogueliteShot2Base) {
        ms.rogueliteShot2Mods.push(card.modifierId);
      }
      // Else: both full, utility only — shouldn't reach here due to pool filtering
    }

    // Track pity
    const isRarePlus = card.rarity === 'rare' || card.rarity === 'epic' || card.rarity === 'legendary';
    ms.roguelitePityCounter = isRarePlus ? 0 : ms.roguelitePityCounter + 1;
    recompileWeapons(ms);
  }

  // Clear draw state
  ms.rogueliteCardDrawOptions = null;
  ms.rogueliteCardDrawType = null;
  ms.rogueliteCardDrawCursor = 0;
}
